import { OffsetElementType } from 'components/AuthPageContentWrapper/AuthPageContextWrapper.types';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { TeamMemberTablePageSize } from 'pages/TeamMemberPage/components/TeamMemberTable/TeamMemberTable.constants';
import { QUERY_teamMemberPage } from 'queries';
import { SessionService } from 'services';
import { JaneEdge, RankingSeniorityDate, UserEmploymentStatusEnum } from 'types/graphql';
import { castEnumToDropdownItems } from 'utils/castEnumsToDropdownItems';

export const teamMemberPageOffsetElements: OffsetElementType[] = [
  'DesktopPageHeaderHeight',
  'SSFTopBarHeight',
  'SSFBottomBarHeight',
];

export const teamMemberPageInternalJaneTypeOptions = castEnumToDropdownItems(
  UserEmploymentStatusEnum,
  'internalJaneType',
);

function payloadTransformer(
  variables: Record<
    string,
    string &
      string[] &
      number[] &
      Record<
        string,
        string | string[] | number[] | Record<string, string | string[] | number[] | boolean>
      >
  >,
  isUserAgencyAndAgencyLocationFilterForcedDisabled: boolean,
) {
  const payload = {
    ...variables,
    ...(SessionService.assertUserType('B2B') &&
      SessionService.assertUserRole('Premium') && { external: false }),
    filter: {
      byFullName: variables.byFullName,
      withServiceableCommunity: [] as number[],
      byAgencyCommunity: [] as number[],
      byEmploymentStatus: variables.internalJaneType,
      byStatus: variables.teamMemberStatus,
      byPosition: variables.position_id,
      byPendingSignup: variables?.teamMemberMobileStatus,
    },
  };
  if (!variables.viewAllFacilities) {
    if (SessionService.assertUserType('B2B'))
      payload.filter.withServiceableCommunity = [
        Number.parseInt(variables.location.value as string),
      ] as number[];

    if (
      SessionService.assertUserType('Agency') &&
      !isUserAgencyAndAgencyLocationFilterForcedDisabled
    )
      payload.filter.byAgencyCommunity = [
        Number.parseInt(variables.location.value as string),
      ] as number[];
  }
  const teamMemberMobileStatus = variables?.teamMemberMobileStatus;

  if (Array.isArray(teamMemberMobileStatus) && teamMemberMobileStatus.length > 0) {
    payload.filter.byPendingSignup = JSON.parse(teamMemberMobileStatus[0]);
  } else {
    payload.filter.byPendingSignup = teamMemberMobileStatus;
  }

  return payload;
}

export const teamMemberPageConfig = ({
  location,
  isUserAgencyAndAgencyLocationFilterForcedDisabled,
}: {
  location: ILocationFilterProps['value'];
  isUserAgencyAndAgencyLocationFilterForcedDisabled: boolean;
}): ISSFBehaviorsOptions => {
  return {
    key: 'TEAM_MEMBER_PAGE',
    pageSize: TeamMemberTablePageSize,
    paginationType: 'PagedPagination',
    query: QUERY_teamMemberPage,
    payloadTransformer: (variables) =>
      payloadTransformer(variables, isUserAgencyAndAgencyLocationFilterForcedDisabled),
    responseTransformer: (data) => {
      if (!data?.fetchJanes?.edges) {
        return data;
      }
      return {
        ...data,
        fetchJanes: {
          ...data.fetchJanes,
          edges: data.fetchJanes.edges.map((edge: JaneEdge) => ({
            ...edge,
            node: {
              ...edge.node,
              ...(edge?.node?.rankingSeniorityDate && {
                rankingSeniorityDate: {
                  ...transformSeniorityDate(edge.node.rankingSeniorityDate),
                },
              }),
            },
          })),
        },
      };
    },
    schema: {
      location: {
        initialValue: location,
        isGlobalCached: true,
        isPreservedOnReset: true,
      },
      teamMemberStatus: {
        initialValue: [],
        isGlobalCached: true,
      },
      teamMemberMobileStatus: {
        initialValue: [],
        isSessionCached: true,
        isGlobalCached: true,
      },
      position_id: {
        initialValue: [],
        isGlobalCached: true,
      },
      viewAllFacilities: {
        initialValue: false,
        isSessionCached: true,
        isGlobalCached: true,
        isPreservedOnReset: true,
      },
      byFullName: {
        initialValue: '',
        isGlobalCached: true,
        isPreservedOnReset: true,
      },
      internalJaneType: {
        initialValue: [],
        isGlobalCached: true,
      },
      sort: {
        initialValue: [
          {
            field: 'accessStatus',
            direction: 'asc',
          },
          {
            field: 'firstName',
            direction: 'asc',
          },
        ],
      },
    },
  };
};

export const transformSeniorityDate = (rankingSeniorityDate: RankingSeniorityDate) => {
  let transformedSeniorityDate: RankingSeniorityDate = {
    seniorityDays: rankingSeniorityDate?.seniorityDays || 0,
    seniorityMonths: rankingSeniorityDate?.seniorityMonths || 0,
    seniorityYears: rankingSeniorityDate?.seniorityYears || 0,
    seniorityHours: rankingSeniorityDate?.seniorityHours || 0,
  };
  let overflowMonths = 0;
  let overflowYears = 0;

  if (rankingSeniorityDate.seniorityDays && rankingSeniorityDate.seniorityDays > 31) {
    overflowMonths = Math.floor(rankingSeniorityDate.seniorityDays / 31);
    transformedSeniorityDate.seniorityDays = rankingSeniorityDate.seniorityDays % 31;
  }

  if (rankingSeniorityDate.seniorityMonths) {
    transformedSeniorityDate.seniorityMonths += overflowMonths;
    if (transformedSeniorityDate.seniorityMonths > 12) {
      overflowYears = Math.floor(transformedSeniorityDate.seniorityMonths / 12);
      transformedSeniorityDate.seniorityMonths = transformedSeniorityDate.seniorityMonths % 12;
    }
  } else if (overflowMonths > 0) {
    transformedSeniorityDate.seniorityMonths = overflowMonths;
  }

  if (rankingSeniorityDate.seniorityYears) {
    transformedSeniorityDate.seniorityYears += overflowYears;
  } else if (overflowYears > 0) {
    transformedSeniorityDate.seniorityYears = overflowYears;
  }
  return transformedSeniorityDate;
};
